<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      fullscreen
      persistent
      scrollable
    >
      <v-card v-if="menu && init">
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar color="indigo">
            <v-icon dark>
              {{ $t(`Restaurant.MenuOptionsMergeDialog.Toolbar.Icon`) }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t(`Restaurant.MenuOptionsMergeDialog.Toolbar.Title`) }}
            </div>

            <div class="text-caption grey--text">
              {{ $t(`Restaurant.MenuOptionsMergeDialog.Toolbar.Subtitle`) }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-3 pb-0">
          <v-container
            class="pa-0"
            fluid
          >
            <!-- Source Product -->
            <v-row dense>
              <v-col>
                <v-container
                  :class="{'pr-2': $vuetify.breakpoint.smAndUp}"
                  class="pa-0"
                  fluid
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <h5 class="mt-0">
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.SourceProduct`) }} #{{ menu.Id }}
                      </h5>
                    </v-col>

                    <v-col class="shrink">
                      <div class="">
                        <div
                          v-if="menu.Image"
                          class="imagecontainer is-contain"
                        >
                          <img
                            :src="appConfig.LOCATION_DATA.CdnImagesUrl + menu.Image"
                            class="rounded-l"
                            height="62"
                            width="auto"
                          >
                        </div>
                      </div>
                    </v-col>

                    <v-col cols="auto">
                      <v-sheet
                        :class="menu.Image ? 'rounded-r' : 'rounded'"
                        :color="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4'"
                        class="pa-2"
                      >
                        <h3 class="info--text">
                          {{ getTranslatedField(menu, 'Name') }}
                        </h3>
                        <div>
                          <template v-for="(category, index) in menu.Categories">
                            <v-chip
                              v-if="getCategoryData(category)"
                              :key="index"
                              :style="'background-color:#' + (getCategoryData(category).Color ? getCategoryData(category).Color : 'e0e0e0') + '; color: ' + (invertColor(getCategoryData(category).Color ? getCategoryData(category).Color : 'e0e0e0', true)) + ';'"
                              class="mr-1 my-1 px-2"
                              label
                              x-small
                            >
                              {{ getTranslatedField(getCategoryData(category), 'Name') }}
                              <span
                                v-if="getCategoryCatalog(category, menu.Catalogs)"
                                class="text-caption ml-1"
                              >- {{ getCategoryCatalog(category, menu.Catalogs) }}</span>
                            </v-chip>
                          </template>
                        </div>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>

              <v-col
                class="text-right shrink pl-1"
              >
                <v-sheet
                  class="pa-2 text-no-wrap"
                  height="95"
                  outlined
                  rounded
                >
                  <div>
                    <span class="font-weight-bold">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.Options.Title`) }}
                    </span>
                    <v-switch
                      v-model="options"
                      :label="options ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0 ml-4 d-inline-flex"
                      color="success"
                      hide-details
                      inset
                    />
                  </div>

                  <div class="mt-1">
                    <span class="font-weight-bold">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.Options.DeleteExisting`) }}
                    </span>
                    <v-switch
                      v-model="deleteExisting"
                      :disabled="!options"
                      :label="deleteExisting ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0 ml-4 d-inline-flex"
                      color="red"
                      hide-details
                      inset
                    />
                  </div>
                </v-sheet>
              </v-col>

              <v-col
                class="text-right shrink pl-1"
              >
                <v-sheet
                  class="pa-2 text-no-wrap"
                  height="95"
                  outlined
                  rounded
                >
                  <div>
                    <span class="font-weight-bold">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.Charges.Title`) }}
                    </span>
                    <v-switch
                      v-model="charges"
                      :label="charges ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0 ml-4 d-inline-flex"
                      color="success"
                      hide-details
                      inset
                    />
                  </div>

                  <div class="mt-1">
                    <span class="font-weight-bold">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.Charges.DeleteExisting`) }}
                    </span>
                    <v-switch
                      v-model="chargesDeleteExisting"
                      :disabled="!charges"
                      :label="chargesDeleteExisting ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0 ml-4 d-inline-flex"
                      color="red"
                      hide-details
                      inset
                    />
                  </div>
                </v-sheet>
              </v-col>

              <v-col
                class="text-right shrink pl-1"
              >
                <v-sheet
                  class="pa-2 text-no-wrap"
                  height="95"
                  outlined
                  rounded
                >
                  <div>
                    <span class="font-weight-bold mr-1">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.SubtractStock.Title`) }}
                    </span>
                    <v-btn-toggle
                      v-model.number="subtractStock"
                      color="success"
                      dense
                      mandatory
                    >
                      <v-btn
                        :value="0"
                        text
                      >
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.SubtractStock.No`).toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :value="1"
                        text
                      >
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.SubtractStock.Enable`).toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :value="2"
                        text
                      >
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.SubtractStock.Disable`).toLocaleUpperCase($i18n.locale) }}
                      </v-btn>
                    </v-btn-toggle>
                  </div>

                  <div class="mt-1">
                    <span class="font-weight-bold mr-1">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.StockQuantity.Title`) }}
                    </span>
                    <v-btn-toggle
                      v-model.number="stockQuantity"
                      color="success"
                      dense
                      mandatory
                    >
                      <v-btn
                        :value="0"
                        text
                      >
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.StockQuantity.No`).toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :value="1"
                        text
                      >
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.StockQuantity.Replace`).toLocaleUpperCase($i18n.locale) }}
                      </v-btn>

                      <v-btn
                        :value="2"
                        text
                      >
                        {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.StockQuantity.Merge`).toLocaleUpperCase($i18n.locale) }}
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </v-sheet>
              </v-col>

              <v-col
                class="text-right shrink pl-1"
              >
                <v-sheet
                  class="pa-2 text-no-wrap"
                  height="95"
                  outlined
                  rounded
                >
                  <div>
                    <span class="font-weight-bold">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.Automation.Status`) }}
                    </span>
                    <v-switch
                      v-model="automationStatus"
                      :disabled="subtractStock !== 1"
                      :label="automationStatus ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0 ml-4 d-inline-flex"
                      color="success"
                      hide-details
                      inset
                    />
                  </div>

                  <div class="mt-1">
                    <span class="font-weight-bold">
                      {{ $t(`Restaurant.MenuOptionsMergeDialog.Body.Automation.Stock`) }}
                    </span>
                    <v-switch
                      v-model="automationStock"
                      :disabled="subtractStock !== 1"
                      :label="automationStock ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                      class="ma-0 ml-4 d-inline-flex"
                      color="success"
                      hide-details
                      inset
                    />
                  </div>
                </v-sheet>
              </v-col>

              <v-col cols="12">
                <v-divider class="my-1" />
              </v-col>
            </v-row>

            <!-- Target Products -->
            <v-row dense>
              <v-col cols="6">
                <h5>
                  {{ mode === 'menu' ? $t(`Restaurant.MenuOptionsMergeDialog.Body.TargetProducts`) : $t(`Restaurant.MenuOptionsMergeDialog.Body.TargetCategories`) }}&nbsp;
                </h5>
              </v-col>

              <v-col cols="6">
                <div class="grey--text font-weight-regular float-right">
                  <v-btn
                    :disabled="!selectedItems.length"
                    class="d-inline-flex mr-2 red px-1"
                    dark
                    depressed
                    x-small
                    @click="removeAllSelectedItems"
                  >
                    {{ $t('Common.Button.Clear') }}
                  </v-btn>

                  {{ selectedItems.length }}
                  {{ mode === 'menu' ? $tc('Restaurant.MenuOptionsMergeDialog.Body.Product', selectedItems.length) : $tc('Restaurant.MenuOptionsMergeDialog.Body.Category', selectedItems.length) }}
                </div>
              </v-col>

              <v-col cols="12">
                <v-container
                  class="pa-0"
                  fluid
                >
                  <v-row
                    dense
                    style="height: 136px; max-height: 136px; overflow-y: auto"
                  >
                    <v-col cols="12">
                      <template v-for="item in selectedItems">
                        <v-chip
                          :key="`item-${item.Id}`"
                          class="mr-1 mb-1 pl-1 pr-2"
                          close
                          label
                          @click:close="removeSelectedItem(item)"
                        >
                          <v-chip
                            class="font-weight-bold mr-2 px-1"
                            color="grey darken-3"
                            dark
                            label
                            small
                          >
                            #{{ item.Id }}
                          </v-chip>

                          {{ getTranslatedField(item, 'Name') }}
                        </v-chip>
                      </template>
                    </v-col>
                  </v-row>
                </v-container>
                <v-divider class="my-2" />
              </v-col>
            </v-row>

            <!-- Available Products -->
            <v-row dense>
              <v-col cols="6">
                <h5>
                  {{ $t(mode === 'menu' ? `Restaurant.MenuOptionsMergeDialog.Body.AvailableProducts` : `Restaurant.MenuOptionsMergeDialog.Body.AvailableCategories`) }}&nbsp;
                </h5>
              </v-col>

              <v-col cols="6">
                <div class="grey--text font-weight-regular float-right">
                  {{ dataTable.total }} {{
                    $tc(mode === 'menu' ? 'Restaurant.MenuOptionsMergeDialog.Body.Product' : 'Restaurant.MenuOptionsMergeDialog.Body.Category', dataTable.total)
                  }}
                </div>
              </v-col>

              <v-col cols="12">
                <v-toolbar
                  :height="$vuetify.breakpoint.xsOnly ? 44 : 60"
                  class="filter-toolbar"
                  flat
                >
                  <v-spacer />

                  <filter-search v-model="dataTable.search.terms" />

                  <filter-options
                    v-model="filtersModel"
                    :filters="filterData"
                  />
                </v-toolbar>

                <v-data-table
                  v-if="mode === 'menu'"
                  :footer-props="dataTable.footerProps"
                  :headers="dataTableHeaders"
                  :height="dataTable.resize.bodyHeight"
                  :items="dataTable.data"
                  :loading="dataTable.loading"
                  :loading-text="dataTable.text.loadingText"
                  :no-data-text="dataTable.text.noDataText"
                  :no-results-text="dataTable.text.noResultsText"
                  :options.sync="dataTable.options"
                  :search="dataTable.search.terms"
                  :server-items-length="dataTable.total"
                  fixed-header
                  item-key="Id"
                >
                  <template #[`item.image`]="{item}">
                    <div style="height: 100%; min-height: 53px; width: 120px; max-width: 120px; text-align: left;">
                      <div
                        v-if="item.Image"
                        class="imagecontainer is-contain"
                        style="padding-top: 8px; position: relative;top: 50%;transform: perspective(1px) translateY(-50%);"
                      >
                        <img
                          :src="appConfig.LOCATION_DATA.CdnImagesUrl + item.Image"
                          class="ml-1"
                          height="51"
                          style=""
                          width="auto"
                        >
                      </div>
                      <div
                        v-else
                        class="imagecontainer is-contain"
                        style="padding-top: 8px; position: relative;top: 50%;transform: perspective(1px) translateY(-50%);"
                      >
                        <img
                          :src="getGMapIcon(LOGO_COLOR.DARK)"
                          class="ml-1 mr-1"
                          height="auto"
                          style="opacity: 0.12;"
                          width="auto"
                        >
                      </div>
                    </div>
                  </template>

                  <template #[`item.menu_id`]="{item}">
                    {{ item.Id }}
                  </template>

                  <template #[`item.menu_name`]="{item}">
                    {{ getTranslatedField(item, 'Name') }}

                    <div>
                      <template v-for="(category, index) in item.Categories">
                        <v-chip
                          v-if="getCategoryData(category)"
                          :key="index"
                          :style="'background-color:#' + (getCategoryData(category).Color ? getCategoryData(category).Color : 'e0e0e0') + '; color: ' + (invertColor(getCategoryData(category).Color ? getCategoryData(category).Color : 'e0e0e0', true)) + ';'"
                          class="mr-1 my-1 px-2"
                          label
                          x-small
                        >
                          {{ getTranslatedField(getCategoryData(category), 'Name') }}
                          <span
                            v-if="getCategoryCatalog(category, item.Catalogs)"
                            class="text-caption ml-1"
                          >- {{ getCategoryCatalog(category, item.Catalogs) }}</span>
                        </v-chip>
                      </template>
                    </div>
                  </template>

                  <template #[`item.catalog`]="{item}">
                    <template v-for="(catalogId, index) in item.Catalogs">
                      <v-chip
                        :key="`catalog-${catalogId}-${index}`"
                        :style="'background-color:#' + (getCatalogById(catalogId).Color ? getCatalogById(catalogId).Color : 'e0e0e0') + '; color: ' + (invertColor(getCatalogById(catalogId).Color ? getCatalogById(catalogId).Color : 'e0e0e0', true)) + ';'"
                        class="mr-1 my-1 px-2"
                        label
                        x-small
                      >
                        {{ getCatalogById(catalogId).Name }}
                      </v-chip>
                    </template>
                  </template>

                  <template #[`item.status`]="{item}">
                    <v-icon
                      v-if="item.Status"
                      color="success"
                    >
                      check_circle
                    </v-icon>
                  </template>

                  <template #[`item.created_at`]="{item}">
                    {{ item.CreatedAt }}
                  </template>

                  <template #[`item.actions`]="{item}">
                    <v-btn
                      v-if="item.Id !== menu?.Id"
                      :class="isItemSelected(item) ? 'orange' : 'info'"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="ma-0 mr-1"
                      dark
                      icon
                      @click="setSelectedItem(item)"
                    >
                      <v-icon :small="$vuetify.breakpoint.xsOnly">
                        {{ isItemSelected(item) ? 'mdi-close' : 'mdi-plus' }}
                      </v-icon>
                    </v-btn>
                  </template>
                </v-data-table>

                <v-data-table
                  v-if="mode==='category'"
                  :footer-props="dataTable.footerProps"
                  :headers="dataTableHeaders"
                  :height="dataTable.resize.bodyHeight"
                  :items="dataTable.data"
                  :loading="dataTable.loading"
                  :loading-text="dataTable.text.loadingText"
                  :no-data-text="dataTable.text.noDataText"
                  :no-results-text="dataTable.text.noResultsText"
                  :options.sync="dataTable.options"
                  :search="dataTable.search.terms"
                  :server-items-length="dataTable.total"
                  fixed-header
                  item-key="Id"
                >
                  <template #[`item.image`]="{item}">
                    <div style="height: 100%; min-height: 53px; width: 120px; max-width: 120px; text-align: left;">
                      <div
                        v-if="item.Color"
                        :style="'background-color: #' + item.Color + ';width:5px; max-width:5px; height: 100%'"
                        class="pa-0 ma-0"
                      />

                      <div
                        v-if="item.Image"
                        class="imagecontainer is-contain"
                        style="padding-top: 8px; position: relative;top: 50%;transform: perspective(1px) translateY(-50%);"
                      >
                        <img
                          :src="appConfig.LOCATION_DATA.CdnImagesUrl + item.Image"
                          class="ml-3"
                          height="51"
                          style=""
                          width="auto"
                        >
                      </div>
                      <div
                        v-else
                        class="imagecontainer is-contain"
                        style="padding-top: 8px; position: relative;top: 50%;transform: perspective(1px) translateY(-50%);"
                      >
                        <img
                          :src="getGMapIcon(LOGO_COLOR.DARK)"
                          class="ml-3 mr-1"
                          height="auto"
                          style="opacity: 0.12;"
                          width="auto"
                        >
                      </div>
                    </div>
                  </template>

                  <template #[`item.category_id`]="{item}">
                    {{ item.Id }}
                  </template>

                  <template #[`item.name`]="{item}">
                    {{ getTranslatedField(item, 'Name') }}

                    <div class="text-caption grey--text">
                      {{ getTranslatedField(item, 'Description') | truncate }}
                    </div>
                  </template>

                  <template #[`item.menus_count`]="{item}">
                    <v-chip
                      label
                      small
                    >
                      {{ item.MenusCount }}
                    </v-chip>
                  </template>

                  <template #[`item.catalog`]="{item}">
                    <template v-for="(catalogId, index) in item.Catalogs">
                      <v-chip
                        :key="`catalog-${catalogId}-${index}`"
                        :style="'background-color:#' + (getCatalogById(catalogId).Color ? getCatalogById(catalogId).Color : 'e0e0e0') + '; color: ' + (invertColor(getCatalogById(catalogId).Color ? getCatalogById(catalogId).Color : 'e0e0e0', true)) + ';'"
                        class="mr-1 my-1 px-2"
                        label
                        x-small
                      >
                        {{ getCatalogById(catalogId).Name }}
                      </v-chip>
                    </template>
                  </template>

                  <template #[`item.status`]="{item}">
                    <v-icon
                      v-if="item.Status"
                      color="success"
                    >
                      check_circle
                    </v-icon>
                  </template>

                  <template #[`item.visible`]="{item}">
                    <v-icon
                      v-if="item.Visible"
                      color="blue"
                    >
                      check_circle
                    </v-icon>
                  </template>

                  <template #[`item.featured`]="{item}">
                    <v-icon
                      v-if="item.Featured"
                      color="cyan"
                    >
                      check_circle
                    </v-icon>
                  </template>

                  <template #[`item.actions`]="{item}">
                    <v-btn
                      v-if="item.Id !== menu?.Id"
                      :class="isItemSelected(item) ? 'orange' : 'info'"
                      :small="$vuetify.breakpoint.xsOnly"
                      class="ma-0 mr-1"
                      dark
                      icon
                      @click="setSelectedItem(item)"
                    >
                      <v-icon :small="$vuetify.breakpoint.xsOnly">
                        {{ isItemSelected(item) ? 'mdi-close' : 'mdi-plus' }}
                      </v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            text
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel') }}
          </v-btn>

          <v-btn
            :disabled="loading || !selectedItems.length"
            :loading="loading"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Restaurant.MenuOptionsMergeDialog.Button.Save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import {clone, invertColor, mergeDeep} from '@/lib/helper/helper.js'
import Auth from '@/mixins/auth.js'
import AppData from '@/mixins/appdata.js'
import DataTable from '@/mixins/data-table/dataTable.js'
import Translatable from '@/mixins/translatable.js'
import FilterSearch from '@/components/common/filter/FilterSearch.vue'
import FilterOptions from '@/components/common/filter/FilterOptions.vue'
import FilterCommon from '@/mixins/filter/filterCommon.js'

export default {
  name      : 'MenuMergeOptionsDialog',
  components: {FilterOptions, FilterSearch},
  mixins    : [Auth, AppData, Translatable, DataTable, FilterCommon],
  props     : {
    menu: {
      type   : Object,
      default: undefined
    },

    categories: {
      type   : Array,
      default: () => []
    },

    catalogs: {
      type   : Array,
      default: () => []
    },

    mode: {
      type   : String,
      default: 'menu'
    },

    returnObject: {
      type   : Boolean,
      default: false
    },

    visible: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      init                 : false,
      loading              : false,
      deleteExisting       : false,
      chargesDeleteExisting: false,
      options              : true,
      charges              : false,
      automationStatus     : false,
      automationStock      : false,
      subtractStock        : 0,
      stockQuantity        : 0,
      selectedItems        : [],
      filtersModel         : null
    }
  },
  computed: {
    categoriesFilterData() {
      return this.categories
    },

    catalogsFilterData() {
      return this.catalogs.map(o => {
        o.Avatar = {Color: `#${o.Color}`}
        return o
      })
    },

    catalogsInternalIds() {
      return this.catalogs.filter(catalog => !catalog.Api && catalog.Status).map(catalog => catalog.Id)
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    'filtersModel.filterCatalog.value': function (newVal) {
      if (this.mode === 'menu') this.filtersModel.filterCategory.items = this.categoriesFilterData.filter(category => newVal?.length ? !!category.Catalogs.find(catalog => newVal.includes(catalog)) : true)
    },

    /**
     * Watcher for the visibility of the dialog.
     * When the dialog becomes visible, it initializes filters, data table configuration,
     * and sets up api event listeners. When hidden, it cleans up listeners and state.
     */
    isVisible(newVal) {
      if (newVal) {
        this.setDataTableByMode()

        this.$nextTick(() => {
          this.setFiltersModeByMode()
          this.initFiltersModel()

          this.deleteExisting = false
          this.chargesDeleteExisting = false
          this.options = true
          this.charges = false
          this.automationStatus = false
          this.automationStock = false
          this.subtractStock = 0
          this.stockQuantity = 0
          this.selectedItem = null

          this.$bus.$on(window.SocketCommand.Menu.MergeOptions, this.onSaveResult)

          this.$nextTick(() => {
            this.initDataTableEvents()
            this.init = true
          })
        })

      } else {
        this.init = false
        this.$bus.$off(window.SocketCommand.Menu.MergeOptions, this.onSaveResult)
        this.destroyDataTableEvents()
      }
    }
  },

  methods: {
    invertColor,

    /**
     * Retrieves category data from the categories array by its ID.
     * @param {number|string} categoryId - The ID of the category to find.
     * @returns {Object|undefined} The category object if found.
     */
    getCategoryData(categoryId) {
      return this.categories.find(categoryObj => parseInt(categoryObj.Id) === parseInt(categoryId))
    },

    /**
     * Configures the filter model based on the current dialog mode ('menu' or 'category').
     * Sets up filter keys, default values, and populates item lists for catalogs and categories.
     */
    setFiltersModeByMode() {
      if (this.mode === 'menu') {
        const defaultData = {
          lang         : 'Restaurant.Menu',
          filterCatalog: {
            key  : 'filter_catalog',
            value: [],
            items: []
          },
          filterCategory: {
            key  : 'filter_category',
            value: [],
            items: []
          },
          filterStatus: {
            key  : 'filter_status',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterStatus.Items')
          },
          filterActive: {
            key  : 'filter_active',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterActive.Items')
          },
          filterActivePos: {
            key  : 'filter_active_pos',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterActivePos.Items')
          },
          filterFeatured: {
            key  : 'filter_featured',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterFeatured.Items')
          },
          filterResetStock: {
            key  : 'filter_reset_stock',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterResetStock.Items')
          },
          filterResetStatus: {
            key  : 'filter_reset_status',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterResetStatus.Items')
          },
          filterCustomPrice: {
            key  : 'filter_custom_price',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterCustomPrice.Items')
          },
          filterCalculatedPrice: {
            key  : 'filter_calculated_price',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterCalculatedPrice.Items')
          },
          filterMarketingPrint: {
            key  : 'filter_marketing_print',
            value: null,
            items: this.$t('Restaurant.Menu.Filter.filterMarketingPrint.Items')
          }
        }

        // Init Custom Filter Values
        defaultData.filterCategory.items = this.categoriesFilterData
        defaultData.filterCatalog.items = this.catalogsFilterData
        defaultData.filterCatalog.value = this.catalogsInternalIds

        this.filtersModel = defaultData
      }

      if (this.mode === 'category') {
        const defaultData = {
          lang         : 'Restaurant.Categories',
          filterCatalog: {
            key  : 'filter_catalog',
            value: [],
            items: []
          },
          filterStatus: {
            key  : 'filter_status',
            value: null,
            items: this.$t('Restaurant.Categories.Filter.filterStatus.Items')
          },
          filterVisible: {
            key  : 'filter_visible',
            value: null,
            items: this.$t('Restaurant.Categories.Filter.filterVisible.Items')
          },
          filterFeatured: {
            key  : 'filter_featured',
            value: null,
            items: this.$t('Restaurant.Categories.Filter.filterFeatured.Items')
          }
        }

        // Init Custom Filter Values
        defaultData.filterCatalog.items = this.catalogsFilterData
        defaultData.filterCatalog.value = this.catalogsInternalIds

        this.filtersModel = defaultData
      }
    },

    /**
     * Configures the data table settings (headers, sorting, resizing) based on whether
     * the dialog is in 'menu' or 'category' mode.
     */
    setDataTableByMode() {
      const menuMode = {
        Id           : '2',
        autoload     : false,
        dataStore    : null,
        socketCommand: {
          list      : window.SocketCommand.Menu.Select,
          listReturn: window.SocketCommand.Menu.Select
        },
        options: {
          defaultSortBy: 'menu_id',
          sortBy       : ['menu_id'],
          sortDesc     : [true]
        },
        resize: {
          enabled   : true,
          offset    : 418,
          bodyHeight: 0
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : '',
            value    : 'image',
            align    : 'left',
            sortable : false,
            class    : 'text-no-wrap',
            cellClass: 'text-caption pa-0',
            width    : 120
          },
          {
            text     : this.$t('Restaurant.MenuOptionsMergeDialog.Table.Headers.Id'),
            value    : 'menu_id',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuOptionsMergeDialog.Table.Headers.Product'),
            value    : 'menu_name',
            align    : 'left',
            sortable : true,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuOptionsMergeDialog.Table.Headers.Status'),
            value    : 'status',
            align    : 'center',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuOptionsMergeDialog.Table.Headers.Catalog'),
            value    : 'catalog',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.MenuOptionsMergeDialog.Table.Headers.Date'),
            value    : 'created_at',
            align    : 'left',
            sortable : false,
            cellClass: 'text-caption'
          },
          {
            text     : '',
            value    : 'actions',
            align    : 'right',
            sortable : false,
            cellClass: 'text-caption'
          }
        ]
      }

      const categoryMode = {
        requestTableConfig: false,
        socketCommand     : {
          list: window.SocketCommand.Category.All
        },
        options: {
          defaultSortBy: 'category_id',
          sortBy       : ['category_id'],
          sortDesc     : [true]
        },
        resize: {
          enabled   : true,
          offset    : 418,
          bodyHeight: 0
        },
        rejectHeaders: {
          xsOnly   : [],
          smAndDown: [],
          mdAndDown: [],
          lgAndDown: [],
          xlAndDown: []
        },
        headers: [
          {
            text     : '',
            value    : 'image',
            align    : 'left',
            sortable : false,
            class    : 'text-no-wrap',
            cellClass: 'text-caption pa-0',
            width    : 120
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Id'),
            value    : 'category_id',
            align    : 'right',
            sortable : true,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Name'),
            value    : 'name',
            align    : 'left',
            sortable : true,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.MenusCount'),
            value    : 'menus_count',
            align    : 'center',
            sortable : true,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Catalog'),
            value    : 'catalog',
            align    : 'left',
            sortable : false,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Status'),
            value    : 'status',
            align    : 'center',
            sortable : true,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Visible'),
            value    : 'visible',
            align    : 'center',
            sortable : true,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Featured'),
            value    : 'featured',
            align    : 'center',
            sortable : true,
            class    : 'text-no-wrap',
            cellClass: 'text-caption'
          },
          {
            text     : this.$t('Restaurant.Categories.Table.Headers.Actions'),
            value    : 'actions',
            align    : 'right',
            sortable : false,
            class    : 'text-no-wrap',
            cellClass: 'text-caption px-2',
            width    : 180
          }
        ]
      }

      if (this.mode === 'menu') {
        menuMode.options.page = 1
        menuMode.total = 0
        menuMode.data = []
        menuMode.loaded = false

        this.dataTable = mergeDeep(this.dataTable, menuMode)
      }

      if (this.mode === 'category') {
        categoryMode.options.page = 1
        categoryMode.total = 0
        categoryMode.data = []
        categoryMode.loaded = false

        this.dataTable = mergeDeep(this.dataTable, categoryMode)
      }
    },

    /**
     * Retrieves the names of catalogs associated with a specific category.
     * @param {number|string} categoryId - The ID of the category.
     * @param {Array} itemCatalogs - List of catalog IDs to filter against.
     * @returns {string} A comma-separated string of catalog names.
     */
    getCategoryCatalog(categoryId, itemCatalogs) {
      const categoryItem = this.getCategoryData(categoryId)
      return itemCatalogs.length > 1 ? this.appConfig?.LOCATION_DATA?.Catalogs?.filter(catalog => categoryItem?.Catalogs?.includes(catalog.Id))?.map(c => c.Name)?.join(', ') || '' : ''
    },

    /**
     * Finds a catalog object from the catalogs prop by its ID.
     * @param {number|string} catalogId - The ID of the catalog to find.
     * @returns {Object|string} The catalog object if found, otherwise an empty string.
     */
    getCatalogById(catalogId) {
      return this.catalogs?.find(c => c.Id === catalogId) || ''
    },

    /**
     * Toggles the selection of an item (Product or Category).
     * If the item is already selected, it removes it; otherwise, it adds it to the list.
     * @param {Object} item - The item to be selected or deselected.
     */
    setSelectedItem(item) {
      if (!item) return
      const obj = clone(item)

      if (this.selectedItems.find(obj => obj.Id === item.Id)) {
        this.removeSelectedItem(obj)
        return
      }

      this.selectedItems.push(obj)
    },

    /**
     * Checks if a specific item is currently in the selected items list.
     * @param {Object} item - The item to check.
     * @returns {boolean} True if the item is selected, false otherwise.
     */
    isItemSelected(item) {
      return !!this.selectedItems.find(obj => obj.Id === item?.Id)
    },

    /**
     * Removes a specific item from the selected items list.
     * @param {Object} item - The item to be removed.
     */
    removeSelectedItem(item) {
      if (!item) return

      const index = this.selectedItems.findIndex(obj => obj.Id === item.Id)
      if (index > -1) this.selectedItems.splice(index, 1)
    },

    removeAllSelectedItems() {
      this.selectedItems = []
    },

    /**
     * Handles the save button click event.
     * Prepares the payload based on the current mode (menu or category) and
     * triggers the merge options api command.
     */
    onSaveButtonClick() {
      this.loading = true
      const menuItem = this.menu ? clone(this.menu) : null
      const selectedItems = clone(this.selectedItems)
      const selectedItemIds = selectedItems.map(item => item.Id)
      const options = this.options ?? false
      const charges = this.charges ?? false
      const deleteExisting = this.deleteExisting ?? false
      const chargesDeleteExisting = this.chargesDeleteExisting ?? false
      const automationStatus = this.automationStatus ?? false
      const automationStock = this.automationStock ?? false
      const stockQuantity = this.stockQuantity ?? 0
      const subtractStock = this.subtractStock ?? 0

      if (this.returnObject) {
        this.$emit('save', this.mode, menuItem, selectedItems)
      } else {
        this.$emit('save', this.mode, menuItem, selectedItemIds)
      }

      if (this.mode === 'menu') {
        window.callAS(window.SocketCommand.Menu.MergeOptions, {
          Id                   : menuItem.Id,
          To                   : selectedItemIds,
          Options              : options,
          DeleteExisting       : deleteExisting,
          Charges              : charges,
          ChargesDeleteExisting: chargesDeleteExisting,
          AutomationStatus     : automationStatus,
          AutomationStock      : automationStock,
          StockQuantity        : stockQuantity,
          SubtractStock        : subtractStock


        })
      } else if (this.mode === 'category') {
        window.callAS(window.SocketCommand.Menu.MergeOptions, {
          Id                   : menuItem.Id,
          Category             : selectedItemIds,
          Options              : options,
          DeleteExisting       : deleteExisting,
          Charges              : charges,
          ChargesDeleteExisting: chargesDeleteExisting,
          AutomationStatus     : automationStatus,
          AutomationStock      : automationStock,
          StockQuantity        : stockQuantity,
          SubtractStock        : subtractStock
        })
      }
    },

    /**
     * Handles the response from the merge options API command.
     * If successful, emits success events and closes the dialog.
     * @param {Object} result - The API response object.
     */
    onSaveResult(result) {
      if (result && result.status === 'success') {
        const menuItem = this.menu ? clone(this.menu) : null
        const selectedItems = clone(this.selectedItems)
        const selectedItemIds = selectedItems.map(item => item.Id)

        if (this.returnObject) {
          this.$emit('save-success', this.mode, menuItem, selectedItems, result)
        } else {
          this.$emit('save-success', this.mode, menuItem, selectedItemIds, result)
        }
        this.onCancelButtonClick()

      } else {
        this.$bus.$emit('app-show-notification', {
          body: result.message,
          type: 'error',
          icon: 'warning'
        })
      }
      this.loading = false
    },

    /**
     * Closes the dialog and resets the component state, including loading status
     * and selected items.
     */
    onCancelButtonClick() {
      this.isVisible = false
      this.loading = false
      this.targetWaiter = null
      this.selectedItems = []
    }
  }
}
</script>

<style scoped>
:deep(.v-list__tile) {
  height: 126px;
}
</style>
